import type { ReactNode } from "react";

import { platformPages, resolvePage } from "../navigation.ts";
import type { Session } from "../session.ts";

/**
 * What this console can and cannot do yet, said out loud.
 *
 * Not a placeholder, and on this console not a footnote either: `16 WP15 Platform Machinery.dc.html`
 * draws sixteen pages and most of them have no operation a browser can call. Each of those carries
 * its own measured reason in `navigation.ts`, re-derived every run by
 * `tests/contract/console-screen-support.test.ts`, and this page is where a platform operator reads
 * them — rather than discovering, one rail item at a time, which parts of the control plane have a
 * working board.
 *
 * **No count is written in this docblock**, and the lead sentence below derives its two from the page
 * list. The version of this file that wrote "fifteen" in three sentences went stale in a single
 * change when three listings were served, while the sentence the operator reads stayed right.
 *
 * It is the right page for a blocked screen for the same reason the rail's hidden-page count excludes
 * them: a page blocked by the platform is not a page an operator can be given permission to see, so
 * telling them "ask whoever administers your roles" would send them to somebody who can do nothing.
 *
 * **A stub pretending to be a board would be the wrong outcome and is the available one.** Every one
 * of those pages has a complete design, a four-tile header, three named columns and a form, and
 * filling them with plausible rows would produce a console that demonstrated well and lied — which
 * is what `CLAUDE.md` section 2 forbids and what the readiness table replaces. Most are waiting on
 * the same two decisions rather than on one piece of work each: a public surface for an internal
 * service (ADR 0029 §2 refuses the gateway as the answer, so each is the owning service's decision —
 * ADRs 0043, 0044, 0045, 0051, 0052 and 0053 are six precedents) and a listing where today there is
 * only a write or a by-identifier read.
 */

export interface PageOutlookProps {
  readonly session: Session;
}

export function PageOutlook({ session }: PageOutlookProps): ReactNode {
  const rows = platformPages.map((page) => ({
    page,
    resolved: resolvePage(page, session.scopes),
  }));
  const blocked = rows.filter(({ page }) => page.blockedBy !== undefined).length;

  return (
    <section className="page">
      <h1 className="page__title">Console readiness</h1>
      <p className="page__lead">
        {`This console draws ${String(rows.length)} pages and ${String(blocked)} of them have no operation behind them yet. Each row below says what is missing, measured against the published contracts rather than remembered.`}
      </p>

      <table className="outlook">
        <thead>
          <tr>
            <th scope="col">Page</th>
            <th scope="col">Your access</th>
            <th scope="col">Waiting on</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ page, resolved }) => (
            <tr key={page.id}>
              <th scope="row">{page.title}</th>
              <td>
                {resolved.access === "forbidden"
                  ? page.blockedBy === undefined
                    ? "No permission"
                    : "Not available"
                  : resolved.access === "read_only"
                    ? "View only"
                    : "Full"}
              </td>
              <td>
                {/*
                 * A page with no recorded reason is a page this console draws, and this row says so
                 * rather than "waiting on a screen" — which is what it used to say, and was wrong
                 * about `audit` from the day that board was built.
                 */}
                {page.blockedBy ??
                  "Nothing. This page is drawn, and reads a listing that spans every tenant."}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
